<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { authFetch } from '@addictivegroup/addictive-auth'
import { api } from '@/lib/http.js'
import { formatMoney } from '@/lib/format.js'
import { reportError } from '@/composables/useSave.js'
import PdfPreview from '@/components/record/PdfPreview.vue'
import UiBadge from '@/components/ui/UiBadge.vue'
import UiModal from '@/components/ui/UiModal.vue'
import UiSpinner from '@/components/ui/UiSpinner.vue'

/**
 * Two revisions compared as the document (D-152): the later one's pages as they print, with
 * what changed since the earlier one marked in them — changed text in the colour of changes,
 * what went struck through — and a numbered badge in the margin where each change is. The
 * list beside says what changed and on which page; a click brings the page into view.
 */
const props = defineProps({
  offerUuid: { type: String, required: true }, from: { type: Number, default: null }, to: { type: Number, default: null }, currency: { type: String, default: 'PLN' },
  // What people call them (R2, R2.1 — D-178); the numbers above only address them.
  fromLabel: { type: String, default: '' }, toLabel: { type: String, default: '' },
})
const emit = defineEmits(['close'])
const { t } = useI18n()
const diff = ref(null)
const pdf = ref('')
const pdfFailed = ref(false)
const pages = ref({})
const active = ref(null)
const viewer = ref(null)
const open = computed(() => props.from !== null && props.to !== null)
// The changes highlighted (D-170): with a look whose colour of changes is its text's, they
// would not stand out — a highlighter for what came, red for what went.
function remembered() {
  try {
    return localStorage.getItem('compare.highlight') !== '0'
  } catch {
    return true
  }
}
const highlight = ref(remembered())
watch(highlight, (v) => {
  try {
    localStorage.setItem('compare.highlight', v ? '1' : '0')
  } catch {
    // private window
  }
  loadPdf()
})

async function load() {
  diff.value = null
  pages.value = {}
  pdfFailed.value = false
  if (pdf.value) URL.revokeObjectURL(pdf.value)
  pdf.value = ''
  if (!open.value) return
  try {
    diff.value = (await api(`/offers/${props.offerUuid}/revisions/${props.from}/compare/${props.to}`)).data
  } catch (e) {
    reportError(e)
    return emit('close')
  }
  await loadPdf()
}
async function loadPdf() {
  if (!open.value) return
  pdfFailed.value = false
  try {
    const response = await authFetch(`/api/v1/offers/${props.offerUuid}/revisions/${props.from}/compare/${props.to}/pdf${highlight.value ? '?highlight=1' : ''}`, { headers: { Accept: 'application/pdf' } })
    if (!response.ok) throw new Error(`${response.status}`)
    if (pdf.value) URL.revokeObjectURL(pdf.value)
    pdf.value = URL.createObjectURL(await response.blob())
  } catch {
    // Chromium busy: the list still says what changed.
    pdfFailed.value = true
  }
}
watch(() => [props.from, props.to], load, { immediate: true })
onBeforeUnmount(() => pdf.value && URL.revokeObjectURL(pdf.value))

const changes = computed(() => (diff.value?.changes || []).map((c, i) => ({ ...c, n: i + 1 })))
// The badge drawn beside each change is its number in the list.
const markers = computed(() => Object.fromEntries(changes.value.map((c) => [c.index, c.n])))
const TYPES = { added: ['green', 'offers.diffAdded'], removed: ['red', 'offers.diffRemoved'], moved: ['gray', 'offers.diffMoved'], text: ['blue', 'offers.diffChanged'], cells: ['blue', 'offers.diffChanged'], parties: ['blue', 'offers.diffChanged'] }
const FIELD_LABELS = { title: 'fields.offer.title', account: 'fields.offer.account', contact: 'fields.offer.contact', end_customer: 'fields.offer.end_customer', site_address: 'fields.offer.site_address', other_site_addresses: 'fields.offer.other_site_addresses', valid_until: 'fields.offer.valid_until', validity_days: 'offers.terms.validity_days', prepared_by: 'fields.offer.prepared_by', preparers: 'fields.offer.prepared_by', our: 'offers.ourContact', terms: 'offers.termsTitle' }
const label = (c) => c.label || t(`catalogue.blockKinds.${c.kind}`)
function go(c) {
  active.value = c.n
  const page = pages.value[c.index]
  if (page) viewer.value?.show(page)
}
</script>

<template>
  <UiModal :open="open" :title="$t('offers.compareTitle', { a: fromLabel || from, b: toLabel || to })" size="screen" @close="emit('close')">
    <div class="grid h-[calc(100vh-9rem)] min-h-0 gap-3 lg:grid-cols-[24rem_minmax(0,1fr)]" data-testid="revision-compare">
      <!-- what changed, and where -->
      <aside class="min-h-0 overflow-y-auto pr-1">
        <div v-if="!diff" class="py-10 text-center"><UiSpinner /></div>
        <template v-else>
          <p class="mb-2 text-sm">
            {{ $t('offers.compareTotal') }}:
            <span class="tabular-nums">{{ formatMoney(diff.totals.from, currency) }}</span> → <strong class="tabular-nums">{{ formatMoney(diff.totals.to, currency) }}</strong>
          </p>
          <p class="mb-3 text-xs text-muted">{{ $t('offers.compareHint', { a: fromLabel || from, b: toLabel || to }) }}</p>
          <label class="mb-3 flex items-center gap-2 rounded-lg border border-default px-2 py-1.5 text-sm" data-testid="compare-highlight">
            <input v-model="highlight" type="checkbox" class="accent-brand-600" />
            <span class="rounded bg-amber-200 px-1 text-slate-900">{{ $t('offers.compareHighlight') }}</span>
          </label>
          <ol class="space-y-2">
            <li v-for="c in changes" :key="c.n">
              <button type="button" :class="['w-full rounded-lg border p-2 text-left text-sm hover:bg-[var(--surface-3)]', active === c.n ? 'border-red-400 bg-red-50 dark:bg-red-950/30' : 'border-default']" data-testid="compare-change" @click="go(c)">
                <span class="flex items-center gap-2">
                  <span class="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-red-600 text-3xs font-bold text-white">{{ c.n }}</span>
                  <UiBadge :tone="TYPES[c.type]?.[0] || 'gray'">{{ $t(TYPES[c.type]?.[1] || 'offers.diffChanged') }}</UiBadge>
                  <span class="min-w-0 flex-1 truncate font-medium">{{ label(c) }}</span>
                  <span v-if="pages[c.index]" class="shrink-0 text-xs text-muted" data-testid="compare-page">{{ $t('offers.comparePage', { n: pages[c.index] }) }}</span>
                  <span v-else-if="pdf && !c.pinned" class="shrink-0 text-xs text-faint">{{ $t('offers.compareNotShown') }}</span>
                </span>
                <span v-if="c.type === 'text' && c.diff" class="mt-1 line-clamp-3 block text-xs leading-relaxed">
                  <template v-for="(part, j) in c.diff" :key="j">
                    <del v-if="part[0] === '-'" class="bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200">{{ part[1] }}</del>
                    <ins v-else-if="part[0] === '+'" class="bg-emerald-100 text-emerald-800 no-underline dark:bg-emerald-900/40 dark:text-emerald-200">{{ part[1] }}</ins>
                    <span v-else-if="part[1].length < 80">{{ part[1] }}</span>
                    <span v-else>{{ part[1].slice(0, 30) }} … {{ part[1].slice(-30) }}</span>
                  </template>
                </span>
                <span v-if="c.type === 'cells'" class="mt-1 block text-xs">
                  <span v-for="cell in c.cells" :key="cell.column" class="mr-2 inline-block">{{ cell.column }}: <del class="text-red-700">{{ cell.from }}</del> → <ins class="text-emerald-700 no-underline">{{ cell.to }}</ins></span>
                </span>
                <span v-if="c.type === 'parties'" class="mt-1 block text-xs text-muted">{{ c.fields.map((f) => $t(FIELD_LABELS[f] || f)).join(', ') }}</span>
              </button>
            </li>
            <li v-if="!changes.length" class="text-sm text-faint">{{ $t('offers.compareNothing') }}</li>
          </ol>
        </template>
      </aside>

      <!-- the later revision's pages, the changes marked -->
      <main class="min-h-0 overflow-y-auto rounded-lg bg-[#e5e7eb] p-4">
        <PdfPreview v-if="pdf" ref="viewer" :src="pdf" :markers="markers" @found="(f) => (pages = f)" />
        <p v-else-if="pdfFailed" class="py-16 text-center text-sm text-muted">{{ $t('offers.pdfUnavailable') }}</p>
        <div v-else class="py-16 text-center"><UiSpinner size="lg" /><p class="mt-2 text-sm text-faint">{{ $t('editor.pdfPrinting') }}</p></div>
      </main>
    </div>
  </UiModal>
</template>
